import React from 'react';

// 🚀 CENTRALIZED SYSTEM ICON LIBRARY (PROPERLY PADDED & UN-CLIPPED)

export const SolidGearIcon = ({ color = "currentColor", size = "16px", style = {} }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 24 24" 
        fill={color || "currentColor"} 
        width={size} 
        height={size} 
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.06-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.73,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.06,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.49-0.12-0.61L19.14,12.94z"/>
    </svg>
);

export const ProfileGearIcon = ({ color, text, style = {} }) => (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', flexShrink: 0, color: color || "currentColor", ...style }}>
        <SolidGearIcon size="32px" />
        {text && <span style={{ position: 'absolute', fontSize: '15px', fontWeight: '900', color: '#ffffff' }}>{text}</span>}
    </div>
);

// ==========================================
// HIGH-DEFINITION 3D EMBOSSED METALLIC GEAR ENGINE
//         <BookGearIcon text="1" colorType="green" size="48px" />
//  Usage: <BookGearIcon text="A" colorType="amber" size="32px" />
//         <BookGearIcon text="!" colorType="red" size="64px" />
// ==========================================
export const BookGearIcon = ({ text, colorType = 'grey', size = '52px', style = {} }) => {
    const rawId = React.useId ? React.useId() : Math.random().toString(36).substring(2, 9);
    const instanceId = rawId.replace(/:/g, '');

    const palettes = {
        grey: {
            top: '#f0f2f5', mid: '#c2c7ce', bot: '#8a9099',
            rimTop: '#b0b5be', rimBot: '#6b7280',
            letter: '#374151', stroke: '#787e87'
        },
        green: {
            top: '#34d399', mid: '#10b981', bot: '#047857',
            rimTop: '#10b981', rimBot: '#065f46',
            letter: '#047857', stroke: '#059669'
        },
        amber: {
            top: '#fcd34d', mid: '#f59e0b', bot: '#b45309',
            rimTop: '#f59e0b', rimBot: '#78350f',
            letter: '#b45309', stroke: '#d97706'
        },
        red: {
            top: '#fca5a5', mid: '#ef4444', bot: '#b91c1c',
            rimTop: '#ef4444', rimBot: '#7f1d1d',
            letter: '#b91c1c', stroke: '#dc2626'
        },
        navy: {
            top: '#3b5a8c', mid: '#213861', bot: '#132240',
            rimTop: '#2b497a', rimBot: '#0d172e',
            letter: '#374151', stroke: '#182a47'
        },
        '#213861': {
            top: '#3b5a8c', mid: '#213861', bot: '#132240',
            rimTop: '#2b497a', rimBot: '#0d172e',
            letter: '#374151', stroke: '#182a47'
        }
    };

    const p = palettes[colorType] || palettes.grey;

    return (
        <svg 
            viewBox="0 0 100 100" 
            width={size} 
            height={size} 
            style={{ 
                width: size, 
                height: size, 
                minWidth: size, 
                minHeight: size, 
                flexShrink: 0, 
                display: 'inline-block', 
                verticalAlign: 'middle', 
                overflow: 'visible', 
                ...style 
            }}
        >
            <defs>
                <linearGradient id={`gear-grad-${instanceId}`} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={p.top} />
                    <stop offset="50%" stopColor={p.mid} />
                    <stop offset="100%" stopColor={p.bot} />
                </linearGradient>

                <linearGradient id={`rim-grad-${instanceId}`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor={p.rimTop} />
                    <stop offset="100%" stopColor={p.rimBot} />
                </linearGradient>

                <linearGradient id={`hub-recess-${instanceId}`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#d1d5db" />
                    <stop offset="35%" stopColor="#e5e7eb" />
                    <stop offset="100%" stopColor="#ffffff" />
                </linearGradient>

                <filter id={`shadow-${instanceId}`} x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="2.5" stdDeviation="2.5" floodColor="#000000" floodOpacity="0.3" />
                </filter>

                <filter id={`inset-shadow-${instanceId}`}>
                    <feOffset dx="0" dy="2.5" />
                    <feGaussianBlur stdDeviation="2" result="offset-blur" />
                    <feComposite operator="out" in="SourceGraphic" in2="offset-blur" result="inverse" />
                    <feFlood floodColor="#000000" floodOpacity="0.45" result="color" />
                    <feComposite operator="in" in="color" in2="inverse" result="shadow" />
                    <feComposite operator="over" in="shadow" in2="SourceGraphic" />
                </filter>
            </defs>

            <g filter={`url(#shadow-${instanceId})`}>
                <g fill={`url(#gear-grad-${instanceId})`} stroke={p.stroke} strokeWidth="1.5" strokeLinejoin="round">
                    <circle cx="50" cy="50" r="37" />
                    {[0, 45, 90, 135, 180, 225, 270, 315].map(deg => (
                        <rect key={deg} x="42.875" y="4.4" width="14.25" height="15.6" rx="2" transform={`rotate(${deg} 50 50)`} />
                    ))}
                    <circle cx="50" cy="50" r="37" />
                </g>

                <circle cx="50" cy="50" r="24" fill={`url(#hub-recess-${instanceId})`} filter={`url(#inset-shadow-${instanceId})`} />
                <circle cx="50" cy="50" r="24" fill="none" stroke={`url(#rim-grad-${instanceId})`} strokeWidth="2" opacity="0.8" />

                <path d="M 26.5,50 A 23.5,23.5 0 0,0 73.5,50" fill="none" stroke="#ffffff" strokeWidth="1.5" opacity="0.9" />

                <text x="50" y="53.5" dominantBaseline="middle" textAnchor="middle" fontSize="27" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif" fill={p.letter}>
                    {text}
                </text>
            </g>
        </svg>
    );
};

export const SignInIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="-16 0 544 512" 
        fill="currentColor"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M416 448h-84c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h84c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32h-84c-6.6 0-12-5.4-12-12V76c0-6.6 5.4-12 12-12h84c44.2 0 80 35.8 80 80v192c0 44.2-35.8 80-80 80zM172.5 356.5l-28.3-28.3c-4.7-4.7-4.7-12.3 0-17l66.8-66.8H24c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h187l-66.8-66.8c-4.7-4.7-4.7-12.3 0-17l28.3-28.3c4.7-4.7 12.3-4.7 17 0l140 140c4.7 4.7 4.7 12.3 0 17l-140 140c-4.7 4.7-12.3 4.7-17 0z"/>
    </svg>
);

export const SignOutIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="-16 0 544 512" 
        fill="currentColor"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M96 64h84c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12H96c-17.7 0-32 14.3-32 32v192c0 17.7 14.3 32 32 32h84c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12H96c-44.2 0-80-35.8-80-80V144c0-44.2 35.8-80 80-80zm243.5 91.5l28.3-28.3c4.7-4.7 12.3-4.7 17 0l140 140c4.7 4.7 4.7 12.3 0 17l-140 140c-4.7 4.7-12.3 4.7-17 0l-28.3-28.3c-4.7-4.7-4.7-12.3 0-17l66.8-66.8H160c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h245.2l-66.7-66.8c-4.8-4.7-4.8-12.3 0-17z"/>
    </svg>
);

export const ChartLineUpIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2.5" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
        <polyline points="16 7 22 7 22 13" />
    </svg>
);

export const RefreshIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2.5" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
        <path d="M21 3v5h-5" />
        <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
        <path d="M3 21v-5h5" />
    </svg>
);

export const WindowCloseIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="-16 0 544 512" 
        fill="currentColor"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M464 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h416c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zm-88 300.7c6.2 6.2 6.2 16.4 0 22.6l-22.6 22.6c-6.2 6.2-16.4 6.2-22.6 0L256 303.3l-74.8 74.6c-6.2 6.2-16.4 6.2-22.6 0l-22.6-22.6c-6.2-6.2-6.2-16.4 0-22.6l74.6-74.8-74.6-74.8c-6.2-6.2-6.2-16.4 0-22.6l22.6-22.6c6.2-6.2 16.4-6.2 22.6 0l74.8 74.6 74.8-74.6c6.2-6.2 16.4-6.2 22.6 0l22.6 22.6c6.2 6.2 6.2 16.4 0 22.6L278.6 256l77.4 76.7z"/>
    </svg>
);

export const FilePdfIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 -128 1536 2048" 
        fill="currentColor"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M1408 1152q0 80-112 121t-262 41q-128 0-272-19-144 122-263 189-106 60-179 60-52 0-82-27.5t-30-74.5q0-54 44-123.5t117-111.5q62-38 128-66 22-83 50.5-195t46.5-188q21-93 29-149-34-106-49-183t-15-115q0-58 19.5-85t54.5-27q38 0 69 31.5t31 92.5q0 31-10 65t-21 68q23 103 62.5 212t82.5 190q108 30 200.5 56t142.5 42q68 20 102 46.5t34 68.5zm-1152 288q0 20 22 20 34 0 110-53-62 13-95.5 22.5t-36.5 10.5zm310-213q208-41 332-60-58-107-107-227-28 111-53.5 204.5t-33.5 158.5zm328-569q13-44 19-81 0-33-8.5-49t-20.5-16q-16 0-25 15.5t-9 47.5q0 40 13 89t30 86zm279 610q0-16-39-31t-121-33q83 31 123 48t37 16zm303-960v1088q0 26-19 45t-45 19h-1280q-26 0-45-19t-19-45v-1408q0-26 19-45t45-19h896l448 448zm-448-320v320h320l-320-320zm320 1376v-960h-352q-14 0-23-9t-9-23v-352h-800v1344h1184z" />
    </svg>
);

export const ShareIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="-32 0 512 512" 
        fill="currentColor"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M352 320c-22.609 0-43.187 8.336-59.199 22.098l-129.223-115.371c2.227-8.422 3.422-17.215 3.422-26.327 0-9.109-1.195-17.902-3.422-26.328l129.223-115.371c16.012 13.762 36.59 22.098 59.199 22.098 53.019 0 96-42.981 96-96s-42.981-96-96-96-96 42.981-96 96c0 9.109 1.195 17.902 3.422 26.328l-129.223 115.371c-16.012-13.762-36.59-22.098-59.199-22.098-53.019 0-96 42.981-96 96s42.981 96 96 96c22.609 0 43.187-8.336 59.199-22.098l129.223 115.371c-2.227 8.422-3.422 17.215-3.422 26.327 0 53.019 42.981 96 96 96s96-42.981 96-96-42.981-96-96-96z" />
    </svg>
);

export const SaveIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 -128 1536 2048" 
        fill="currentColor"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M1408 384v1088q0 26-19 45t-45 19h-1152q-26 0-45-19t-19-45v-1280q0-26 19-45t45-19h896l448 448zm-768-128v384h384v-384h-384zm640 1216v-512q0-26-19-45t-45-19h-768q-26 0-45 19t-19 45v512h896zm128-1088l-256-256v256h256z" />
    </svg>
);

export const CloseIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="-16 -16 544 544" 
        fill="currentColor"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M256 8C119 8 0 127 0 256s119 248 256 248 256-107 256-248S393 8 256 8zm121.6 313.1c4.7 4.7 4.7 12.3 0 17l-36.2 35.9c-4.7 4.7-12.3 4.7-17 0L256 305.9l-68.4 68.1c-4.7 4.7-12.3 4.7-17 0l-36.2-35.9c-4.7-4.7-4.7-12.3 0-17l68.4-68.1-68.4-68.1c-4.7-4.7-4.7-12.3 0-17l36.2-35.9c4.7-4.7 12.3-4.7 17 0l68.4 68.1 68.4-68.1c4.7-4.7 12.3-4.7 17 0l36.2 35.9c4.7 4.7 4.7 12.3 0 17L305.9 256l67.7 65.1z" />
    </svg>
);



export const ArrowCircleUpIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="-16 -16 544 544" 
        fill="currentColor"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path 
            fillRule="evenodd"
            d="M256 8C117.4 8 8 117.4 8 256s109.4 248 248 248 248-109.4 248-248S394.6 8 256 8zm-32 352c0 13.3 10.7 24 24 24h16c13.3 0 24-10.7 24-24V256h40c17.8 0 26.7-21.5 14.1-34.1L270.1 149.9c-7.8-7.8-20.5-7.8-28.3 0L169.9 221.9C157.3 234.5 166.2 256 184 256h40v104z" 
        />
    </svg>
);

export const ArrowCircleDownIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="-16 -16 544 544" 
        fill="currentColor"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path 
            transform="rotate(180 256 256)"
            fillRule="evenodd"
            d="M256 8C117.4 8 8 117.4 8 256s109.4 248 248 248 248-109.4 248-248S394.6 8 256 8zm-32 352c0 13.3 10.7 24 24 24h16c13.3 0 24-10.7 24-24V256h40c17.8 0 26.7-21.5 14.1-34.1L270.1 149.9c-7.8-7.8-20.5-7.8-28.3 0L169.9 221.9C157.3 234.5 166.2 256 184 256h40v104z" 
        />
    </svg>
);

export const ArrowCircleLeftIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="-16 -16 544 544" 
        fill="currentColor"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path 
            transform="rotate(-90 256 256)"
            fillRule="evenodd"
            d="M256 8C117.4 8 8 117.4 8 256s109.4 248 248 248 248-109.4 248-248S394.6 8 256 8zm-32 352c0 13.3 10.7 24 24 24h16c13.3 0 24-10.7 24-24V256h40c17.8 0 26.7-21.5 14.1-34.1L270.1 149.9c-7.8-7.8-20.5-7.8-28.3 0L169.9 221.9C157.3 234.5 166.2 256 184 256h40v104z" 
        />
    </svg>
);

export const ArrowCircleRightIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="-16 -16 544 544" 
        fill="currentColor"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path 
            transform="rotate(90 256 256)"
            fillRule="evenodd"
            d="M256 8C117.4 8 8 117.4 8 256s109.4 248 248 248 248-109.4 248-248S394.6 8 256 8zm-32 352c0 13.3 10.7 24 24 24h16c13.3 0 24-10.7 24-24V256h40c17.8 0 26.7-21.5 14.1-34.1L270.1 149.9c-7.8-7.8-20.5-7.8-28.3 0L169.9 221.9C157.3 234.5 166.2 256 184 256h40v104z" 
        />
    </svg>
);

export const ArrowLeftLongIcon = ({ size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill="currentColor"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M440 208H88.1l100.1-100.1c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-155 155c-12.5 12.5-12.5 32.8 0 45.3l155 155c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L88.1 272H440c17.7 0 32-14.3 32-32s-14.3-32-32-32z"/>
    </svg>
);

// ==========================================
// FONT AWESOME CIRCLE DIGIT ICONS (0-9)
// ==========================================

export const Circle0Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="256" cy="256" r="256" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">0</text>
    </svg>
);

export const Circle1Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="256" cy="256" r="256" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">1</text>
    </svg>
);

export const Circle2Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="256" cy="256" r="256" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">2</text>
    </svg>
);

export const Circle3Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="256" cy="256" r="256" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">3</text>
    </svg>
);

export const Circle4Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="256" cy="256" r="256" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">4</text>
    </svg>
);

export const Circle5Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="256" cy="256" r="256" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">5</text>
    </svg>
);

export const Circle6Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="256" cy="256" r="256" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">6</text>
    </svg>
);

export const Circle7Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="256" cy="256" r="256" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">7</text>
    </svg>
);

export const Circle8Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="256" cy="256" r="256" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">8</text>
    </svg>
);

export const Circle9Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="256" cy="256" r="256" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">9</text>
    </svg>
);


// ==========================================
// FONT AWESOME HUNDRED POINTS & SQUARE DIGIT ICONS (0-9)
// ==========================================

export const HundredPointsIcon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <g transform="skewX(-12) translate(32, 0)">
            <text x="210" y="230" textAnchor="middle" dominantBaseline="central" fontSize="260" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">100</text>
        </g>
        <path d="M 80 395 L 432 395" stroke={color || "currentColor"} strokeWidth="36" strokeLinecap="round" />
        <path d="M 140 455 L 372 455" stroke={color || "currentColor"} strokeWidth="36" strokeLinecap="round" />
    </svg>
);

export const Square0Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <rect x="32" y="32" width="448" height="448" rx="96" ry="96" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">0</text>
    </svg>
);

export const Square1Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <rect x="32" y="32" width="448" height="448" rx="96" ry="96" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">1</text>
    </svg>
);

export const Square2Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <rect x="32" y="32" width="448" height="448" rx="96" ry="96" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">2</text>
    </svg>
);

export const Square3Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <rect x="32" y="32" width="448" height="448" rx="96" ry="96" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">3</text>
    </svg>
);

export const Square4Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <rect x="32" y="32" width="448" height="448" rx="96" ry="96" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">4</text>
    </svg>
);

export const Square5Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <rect x="32" y="32" width="448" height="448" rx="96" ry="96" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">5</text>
    </svg>
);

export const Square6Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <rect x="32" y="32" width="448" height="448" rx="96" ry="96" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">6</text>
    </svg>
);

export const Square7Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <rect x="32" y="32" width="448" height="448" rx="96" ry="96" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">7</text>
    </svg>
);

export const Square8Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <rect x="32" y="32" width="448" height="448" rx="96" ry="96" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">8</text>
    </svg>
);

export const Square9Icon = ({ color, size = "18px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color || "currentColor"}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <rect x="32" y="32" width="448" height="448" rx="96" ry="96" />
        <text x="256" y="268" textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize="310" fontWeight="900" fontFamily="Inter, system-ui, -apple-system, sans-serif">9</text>
    </svg>
);

// 🚀 FONT AWESOME FILE IMPORT & EXPORT SVG ICONS
export const FileImportIcon = ({ size = "18px", color = "currentColor", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M16 64C16 28.7 44.7 0 80 0H224V128c0 17.7 14.3 32 32 32H384V288H336c-17.7 0-32 14.3-32 32v32c0 17.7 14.3 32 32 32h48V448c0 35.3-28.7 64-64 64H80c-35.3 0-64-28.7-64-64V64zM384 128H256V0L384 128zM288 352c0-8.8-7.2-16-16-16H160V288c0-7.1-4.7-13.4-11.5-15.5s-14.3 .1-18.9 5.5l-64 72c-4.1 4.8-4.1 11.7 0 16.5l64 72c4.6 5.4 12.1 7.6 18.9 5.5s11.5-8.4 11.5-15.5V384h112c8.8 0 16-7.2 16-16z"/>
    </svg>
);

export const FileExportIcon = ({ size = "18px", color = "currentColor", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M16 64C16 28.7 44.7 0 80 0H224V128c0 17.7 14.3 32 32 32H384V288H336c-17.7 0-32 14.3-32 32v32c0 17.7 14.3 32 32 32h48V448c0 35.3-28.7 64-64 64H80c-35.3 0-64-28.7-64-64V64zM384 128H256V0L384 128zM0 352c0 8.8 7.2 16 16 16H128v48c0 7.1 4.7 13.4 11.5 15.5s14.3-.1 18.9-5.5l64-72c4.1-4.8 4.1-11.7 0-16.5l-64-72c-4.6-5.4-12.1-7.6-18.9-5.5S128 280.9 128 288v32H16c-8.8 0-16 7.2-16 16z"/>
    </svg>
);

export const CopyClipboardIcon = ({ size = "16px", color = "currentColor", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke={color} 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
);

export const BlockQuestionIcon = ({ color = "currentColor", size = "16px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M96 32C60.7 32 32 60.7 32 96v320c0 35.3 28.7 64 64 64h320c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H96zM64 96c0-17.7 14.3-32 32-32h320c17.7 0 32 14.3 32 32v320c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V96zm64 80a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm256 0a24 24 0 1 0 0-48 24 24 0 1 0 0 48zM128 384a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm256 0a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm-177-193.2c11-13.8 28.1-22.8 49-22.8c30.9 0 56 25.1 56 56c0 16.5-7.1 31.4-18.4 41.7l-4.7 4.3c-7.9 7.2-12.9 17.1-14.2 28.1l-1.9 15c-1.6 13.1-13.4 22.9-26.6 22.9c-14.8 0-26.8-12-26.8-26.8v-5c0-15.6 5.8-30.6 16.4-42.1l7.8-8.5c6.2-6.7 10.4-15.6 10.4-25.5c0-13.3-10.7-24-24-24c-13 0-23.6 10.2-24 23v3.7c0 13.3-10.7 24-24 24c-13.3 0-24-10.7-24-24v-3.7c.9-32 23.3-59.5 53.6-70.6zM256 400a24 24 0 1 0 0-48 24 24 0 1 0 0 48z"/>
    </svg>
);

// ==========================================
// 🏛️ EXECUTIVE FontAwesome MEETING ALTITUDE ICONS
// ==========================================

// Sharp Solid Chess (Quarterly Off-site)
export const OffsiteMeetingIcon = ({ color = "currentColor", size = "22px" }) => (
    <svg viewBox="0 0 512 512" width={size} height={size} style={{ width: size, height: size, flexShrink: 0, fill: color, display: 'inline-block', verticalAlign: 'middle' }}>
        {/* King Piece */}
        <path d="M236 32h40v36h36v44h-36v28h52l20 60h-36c0 32-16 64-32 92l68 128H140l68-128c-16-28-32-60-32-92h-36l20-60h52V112h-36V68h36V32zM116 440h280v40H116v-40z"/>
        {/* Rook Piece */}
        <path d="M388 240h28v28h20v-28h28v28h20v-28h28v80l-20 32v80h16v40H364v-40h16v-80l-20-32v-32c12 0 24-8 28-20z"/>
    </svg>
);

// Classic Solid Bullseye (Strategic Adhoc)
export const StrategicMeetingIcon = ({ color = "currentColor", size = "22px" }) => (
    <svg viewBox="0 0 512 512" width={size} height={size} style={{ width: size, height: size, flexShrink: 0, fill: color, display: 'inline-block', verticalAlign: 'middle' }}>
        <path fillRule="evenodd" clipRule="evenodd" d="
            M 256 0 C 397.38 0 512 114.62 512 256 C 512 397.38 397.38 512 256 512 C 114.62 512 0 397.38 0 256 C 0 114.62 114.62 0 256 0 Z
            M 256 64 C 150 64 64 150 64 256 C 64 362 150 448 256 448 C 362 448 448 362 448 256 C 448 150 362 64 256 64 Z
            M 256 128 C 326.69 128 384 185.31 384 256 C 384 326.69 326.69 384 256 384 C 185.31 384 128 326.69 128 256 C 128 185.31 185.31 128 256 128 Z
            M 256 192 C 220.65 192 192 220.65 192 256 C 192 291.35 220.65 320 256 320 C 291.35 320 320 291.35 320 256 C 320 220.65 291.35 192 256 192 Z
            M 256 224 C 273.67 224 288 238.33 288 256 C 288 273.67 273.67 288 256 288 C 238.33 288 224 273.67 224 256 C 224 238.33 238.33 224 256 224 Z
        " />
    </svg>
);

// Sharp Duotone Rocket Vertical (Weekly Tactical)
export const TacticalMeetingIcon = ({ color = "currentColor", size = "22px" }) => (
    <svg viewBox="0 0 512 512" width={size} height={size} style={{ width: size, height: size, flexShrink: 0, display: 'inline-block', verticalAlign: 'middle' }}>
        {/* Secondary Duotone Fins Layer (0.45 Opacity) */}
        <path fill={color} opacity="0.45" d="M 204 272 L 128 344 L 156 456 L 232 384 Z M 308 272 L 384 344 L 356 456 L 280 384 Z" />
        {/* Primary Rocket Body Layer with Porthole Cutout */}
        <path fill={color} fillRule="evenodd" clipRule="evenodd" d="
            M 256 40
            C 336 140 336 270 272 384
            H 240
            C 176 270 176 140 256 40 Z
            M 256 184
            a 32 32 0 1 0 0.01 0 Z
        " />
    </svg>
);

// Duotone Regular Calendar Days (Daily Stand-up - 6 Grid Slots)
export const DailyStandupIcon = ({ color = "currentColor", size = "22px" }) => (
    <svg viewBox="0 0 448 512" width={size} height={size} style={{ width: size, height: size, flexShrink: 0, fill: color, display: 'inline-block', verticalAlign: 'middle' }}>
        <path fillRule="evenodd" clipRule="evenodd" d="
            M 96 80 h 256 a 64 64 0 0 1 64 64 v 320 a 64 64 0 0 1 -64 64 H 96 a 64 64 0 0 1 -64 -64 V 144 a 64 64 0 0 1 64 -64 Z
            M 112 16 h 48 a 24 24 0 0 1 24 24 v 48 h -96 V 40 a 24 24 0 0 1 24 -24 Z
            M 288 16 h 48 a 24 24 0 0 1 24 24 v 48 h -96 V 40 a 24 24 0 0 1 24 -24 Z
            M 64 144 h 320 a 12 12 0 0 1 12 12 v 32 a 12 12 0 0 1 -12 12 H 64 a 12 12 0 0 1 -12 -12 v -32 a 12 12 0 0 1 12 -12 Z
            M 68 232 h 84 a 12 12 0 0 1 12 12 v 52 a 12 12 0 0 1 -12 12 H 68 a 12 12 0 0 1 -12 -12 v -52 a 12 12 0 0 1 12 -12 Z
            M 182 232 h 84 a 12 12 0 0 1 12 12 v 52 a 12 12 0 0 1 -12 12 h -84 a 12 12 0 0 1 -12 -12 v -52 a 12 12 0 0 1 12 -12 Z
            M 296 232 h 84 a 12 12 0 0 1 12 12 v 52 a 12 12 0 0 1 -12 12 h -84 a 12 12 0 0 1 -12 -12 v -52 a 12 12 0 0 1 12 -12 Z
            M 68 336 h 84 a 12 12 0 0 1 12 12 v 52 a 12 12 0 0 1 -12 12 H 68 a 12 12 0 0 1 -12 -12 v -52 a 12 12 0 0 1 12 -12 Z
            M 182 336 h 84 a 12 12 0 0 1 12 12 v 52 a 12 12 0 0 1 -12 12 h -84 a 12 12 0 0 1 -12 -12 v -52 a 12 12 0 0 1 12 -12 Z
            M 296 336 h 84 a 12 12 0 0 1 12 12 v 52 a 12 12 0 0 1 -12 12 h -84 a 12 12 0 0 1 -12 -12 v -52 a 12 12 0 0 1 12 -12 Z
        " />
    </svg>
);

// 🚀 CUTOUT MASKED SOLID SQUARE EXCLAMATION & SQUARE QUESTION ICONS
export const SquareExclamationIcon = ({ size = "18px", color = "currentColor", style = {} }) => {
    const rawId = React.useId ? React.useId() : Math.random().toString(36).substring(2, 9);
    const maskId = `sq-excl-mask-${rawId.replace(/:/g, '')}`;

    return (
        <svg 
            width={size} 
            height={size} 
            viewBox="0 0 512 512" 
            style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
        >
            <defs>
                <mask id={maskId}>
                    {/* White base allows rect to render */}
                    <rect x="0" y="0" width="512" height="512" fill="#ffffff" />
                    {/* Black shapes punch transparent holes through the rect */}
                    <path d="M 222 150 C 222 131 237 116 256 116 C 275 116 290 131 290 150 L 278 278 C 278 290 268 300 256 300 C 244 300 234 290 234 278 Z" fill="#000000" />
                    <circle cx="256" cy="368" r="32" fill="#000000" />
                </mask>
            </defs>
            <rect x="32" y="32" width="448" height="448" rx="96" ry="96" fill={color || "currentColor"} mask={`url(#${maskId})`} />
        </svg>
    );
};

export const SquareQuestionIcon = ({ size = "18px", color = "currentColor", style = {} }) => {
    const rawId = React.useId ? React.useId() : Math.random().toString(36).substring(2, 9);
    const maskId = `sq-quest-mask-${rawId.replace(/:/g, '')}`;

    return (
        <svg 
            width={size} 
            height={size} 
            viewBox="0 0 512 512" 
            style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
        >
            <defs>
                <mask id={maskId}>
                    {/* White base allows rect to render */}
                    <rect x="0" y="0" width="512" height="512" fill="#ffffff" />
                    {/* Black shapes punch transparent holes through the rect */}
                    <path d="M 256 116 C 194 116 154 156 150 206 C 149 219 159 230 172 230 C 185 230 195 220 197 208 C 200 181 221 160 256 160 C 289 160 310 178 310 205 C 310 223 296 238 275 254 C 245 276 230 298 230 328 L 230 330 C 230 344 241 354 256 354 C 271 354 282 344 282 330 L 282 328 C 282 308 295 295 314 280 C 344 257 364 231 364 205 C 364 147 318 116 256 116 Z" fill="#000000" />
                    <circle cx="256" cy="396" r="32" fill="#000000" />
                </mask>
            </defs>
            <rect x="32" y="32" width="448" height="448" rx="96" ry="96" fill={color || "currentColor"} mask={`url(#${maskId})`} />
        </svg>
    );
};

export const AltitudeIcon = ({ color = "currentColor", size = "24px", className = "", style = {} }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 512 512" 
        width={size} 
        height={size} 
        fill="none" 
        stroke={color} 
        strokeWidth="20"
        strokeLinecap="round" 
        strokeLinejoin="round"
        className={className}
        style={{ minWidth: size, minHeight: size, flexShrink: 0, ...style }}
    >
        {/* Outer Ring */}
        <circle cx="256" cy="256" r="225" />
        
        {/* Inner Ring */}
        <circle cx="256" cy="256" r="175" />
        
        {/* Left Side Wedge */}
        <path d="M 92 225 L 125 256 L 92 287" />
        
        {/* Right Side Wedge */}
        <path d="M 420 225 L 387 256 L 420 287" />
        
        {/* Center Horizon Line */}
        <line x1="125" y1="256" x2="387" y2="256" />
        
        {/* Center Pitch Indicator Triangle */}
        <path d="M 210 256 L 256 195 L 302 256 Z" />
        
        {/* Upper Pitch Ladder Marks */}
        <line x1="225" y1="115" x2="287" y2="115" />
        <line x1="190" y1="150" x2="322" y2="150" />
        <line x1="225" y1="185" x2="287" y2="185" />
        
        {/* Lower Pitch Ladder Marks */}
        <line x1="225" y1="327" x2="287" y2="327" />
        <line x1="190" y1="362" x2="322" y2="362" />
        <line x1="225" y1="397" x2="287" y2="397" />
    </svg>
);

// 🚀 MICRO-OPTIMIZED ALTITUDE GAP ICON (DESIGNED FOR 12PX-16PX PILLS)
export const AltitudeGapIcon = ({ color = "currentColor", size = "14px", style = {} }) => (
    <svg 
        viewBox="0 0 24 24" 
        width={size} 
        height={size} 
        fill="none" 
        stroke={color} 
        strokeWidth="2.2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        {/* Crisp Micro-Altimeter Ring */}
        <circle cx="12" cy="12" r="9.5" strokeWidth="2" />
        {/* Center Horizon Divider */}
        <line x1="5.5" y1="12" x2="18.5" y2="12" strokeWidth="1.8" />
        {/* High Altitude Chevron (30k ft) */}
        <path d="M 12 5.5 L 15 9.5 H 9 Z" fill={color} stroke="none" />
        {/* Low Altitude Chevron (5k ft) */}
        <path d="M 12 18.5 L 9 14.5 H 15 Z" fill={color} stroke="none" />
    </svg>
);

export const MapPinIcon = ({ color = "currentColor", size = "16px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 384 512" 
        fill={color}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/>
    </svg>
);

export const PhoneIcon = ({ color = "currentColor", size = "16px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 165c13.7-11.1 18.4-30 11.6-46.3l-40-94.1z"/>
    </svg>
);

export const EmailIcon = ({ color = "currentColor", size = "16px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke={color} 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
);

export const GlobeIcon = ({ color = "currentColor", size = "16px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke={color} 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
);

export const ClockIcon = ({ color = "currentColor", size = "16px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke={color} 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

export const WheelchairIcon = ({ color = "currentColor", size = "16px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke={color} 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="12" cy="4" r="2" fill={color} />
        <path d="M5 11a7 7 0 1 0 14 0" />
        <path d="M11 8v6h5l3 5" />
    </svg>
);

export const PlusCircleIcon = ({ color = "currentColor", size = "16px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke={color} 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
);

export const WarningTriangleIcon = ({ color = "currentColor", size = "16px", style = {} }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 512 512" 
        fill={color}
        style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible', ...style }}
    >
        <path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S488.7 480 474.5 480H37.5c-14.2 0-27.3-7.5-34.5-19.8s-7.3-27.7-.2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24v112c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"/>
    </svg>
);

// ⚡ STATIC SVG STRING GENERATORS (FOR MAPBOX POPUP HTML STRINGS)
export const iconSvg = {
  mapPin: (color = "#2563eb", size = 14) => `<svg width="${size}" height="${size}" viewBox="0 0 384 512" fill="${color}" style="display:inline-block;vertical-align:-0.15em;margin-right:4px;flex-shrink:0;"><path d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/></svg>`,
  phone: (color = "#2563eb", size = 14) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-0.15em;margin-right:4px;flex-shrink:0;"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.7 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`,
  email: (color = "#2563eb", size = 14) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-0.15em;margin-right:4px;flex-shrink:0;"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>`,
  clock: (color = "#2563eb", size = 14) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-0.15em;margin-right:4px;flex-shrink:0;"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
  wheelchair: (color = "#2563eb", size = 14) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-0.15em;margin-right:4px;flex-shrink:0;"><circle cx="12" cy="4" r="2" fill="${color}"/><path d="M5 11a7 7 0 1 0 14 0"/><path d="M11 8v6h5l3 5"/></svg>`,
  globe: (color = "#2563eb", size = 14) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-0.15em;margin-right:4px;flex-shrink:0;"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
  plusCircle: (color = "#2563eb", size = 14) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-0.15em;margin-right:4px;flex-shrink:0;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`
};